import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import Paper from './common/paper';
import { getDetailText as getDetailTextAction } from './redux/modules/detailText';

/** Normalize different response shapes to the actual detailText doc.
 *  Note: our reducer already stores the *doc itself* at state.detailText.payload.
 *  We keep this helper exported for other call sites, but it isn't used by this component anymore.
 */
function extractDetailDoc(resOrPayload) {
  const payload =
    (resOrPayload && resOrPayload.payload) || resOrPayload;
  const body =
    (payload && payload.data) ||
    (payload && payload.body) ||
    payload;

  if (body && body.detailText) return body.detailText; // { status, detailText }
  if (body && body.text) return body;                  // doc directly
  return null;
}

/** If the parent doesn't pass filter, derive it from router props */
function getFilterFromProps(props) {
  const m = props && props.match;
  return decodeURIComponent((m && m.params && m.params.filter) || '');
}

/**
 * Reusable section that renders the detailText (HTML) for an offer type.
 * Props:
 * - offerType: string (required) e.g. "loans"
 * - filter: string (optional) – if omitted, read from route param :filter
 * - feed, location: same semantics as before (to support feed_only=true hide)
 * - fallback: React node to render when there's no detailHtml and not loading
 * - wrapInPaper: bool (default true) – controls Paper wrapper
 */
class DetailTextSection extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // On SSR, initialDetailDoc will be present so we render HTML on first paint.
      detailDoc: props.initialDetailDoc || null,
      detailLoading: false
    };
  }

  componentDidMount() {
    const filter = this.props.filter || getFilterFromProps(this.props);
    // Fetch when URL filter differs from what Redux last loaded
    if (filter && filter !== this.props.currentFilter) {
      this.fetchDetail(filter);
    }
  }

  componentDidUpdate(prevProps) {
    const prevFilterProp = prevProps.filter || getFilterFromProps(prevProps);
    const nextFilterProp = this.props.filter || getFilterFromProps(this.props);

    // Refetch when the :filter changes on the client
    if (prevFilterProp !== nextFilterProp && nextFilterProp) {
      this.fetchDetail(nextFilterProp);
    }

    // If Redux delivers a new initialDetailDoc (e.g., after navigation), adopt it
    if (this.props.initialDetailDoc !== prevProps.initialDetailDoc) {
      this.setState({ detailDoc: this.props.initialDetailDoc });
    }
  }

  fetchDetail(filter) {
    const { offerType, getDetailText } = this.props;
    this.setState({ detailLoading: true });
    getDetailText(offerType, filter)
      .then(res => {
        // getDetailText returns the HTTP carrier; normalize to doc for local state
        const doc = extractDetailDoc(res);
        this.setState({ detailDoc: doc, detailLoading: false });
      })
      .catch(() => this.setState({ detailLoading: false }));
  }

  renderContent() {
    const { detailDoc, detailLoading } = this.state;
    const detailHtml = detailDoc && detailDoc.text ? detailDoc.text : null;
    const { fallback, wrapInPaper = true } = this.props;

    if (detailHtml) {
      const htmlBlock = (
        <div
          // Inject raw HTML so it appears in server-rendered markup (for View Source/SEO)
          dangerouslySetInnerHTML={{ __html: detailHtml }}
        />
      );
      return wrapInPaper ? <Paper>{htmlBlock}</Paper> : htmlBlock;
    }

    if (detailLoading) {
      // Optionally show fallback while loading to avoid layout jumps
      return fallback || null;
    }

    return fallback || null;
  }

  render() {
    const { location, feed } = this.props;

    // Preserve original “feed_only=true” short-circuit
    if ((/feed_only=true/.test((location && location.search) || '')) || feed) {
      return null;
    }

    return this.renderContent();
  }
}

// Pull initial doc from Redux (e.g., SSR) and the last loaded filter.
const mapStateToProps = state => ({
  initialDetailDoc:
    (state && state.detailText && state.detailText.payload) || null,
  currentFilter:
    (state && state.detailText && state.detailText.filter) || null
});

export default withRouter(
  connect(mapStateToProps, { getDetailText: getDetailTextAction })(DetailTextSection)
);

export { extractDetailDoc };
